import type { Issue, IssueCategory, Report } from '#lib/types';
import { i18n, t } from '#lib/i18n/state.svelte';
import { issueTextEn } from '#lib/i18n/issues';

export type RevisionStatus = 'corrected' | 'clarified' | 'no_change' | 'acknowledged';

export interface RevisionEntry {
	issue: Issue;
	response: string;
	status: RevisionStatus;
}

export const REVISION_STATUSES: RevisionStatus[] = [
	'corrected',
	'clarified',
	'no_change',
	'acknowledged'
];

const CATEGORY_ORDER: IssueCategory[] = [
	'statistics',
	'citations',
	'structure',
	'language',
	'access',
	'provenance'
];

/** Mektuba yalnızca kararlı ve info olmayan bulgular girer. */
export function selectRevisionIssues(report: Report): Issue[] {
	return report.issues
		.filter((i) => i.decisive && i.severity !== 'info' && i.severity !== 'ok')
		.sort((a, b) => {
			const ca = CATEGORY_ORDER.indexOf(a.category);
			const cb = CATEGORY_ORDER.indexOf(b.category);
			if (ca !== cb) return ca - cb;
			const rank = { critical: 0, warning: 1, info: 2, ok: 3 } as const;
			return rank[a.severity] - rank[b.severity];
		});
}

export function makeEntries(report: Report): RevisionEntry[] {
	return selectRevisionIssues(report).map((issue) => ({
		issue,
		response: '',
		status: 'corrected'
	}));
}

function localized(issue: Issue): { title: string; detail: string } {
	const en = i18n.lang === 'en' ? issueTextEn(issue) : null;
	return { title: en?.title ?? issue.title, detail: en?.detail ?? issue.detail };
}

function location(issue: Issue): string {
	if (issue.location.page !== undefined) return ` (${t('common.page', { n: issue.location.page })})`;
	if (issue.location.sourceLine !== undefined)
		return ` (${t('common.line', { n: issue.location.sourceLine })})`;
	return '';
}

export function buildRevisionLetter(entries: RevisionEntry[]): string {
	const greeting = t('revision.letter.greeting');
	const intro = t('revision.letter.intro');
	const closing = t('revision.letter.closing');
	const signoff = t('revision.letter.signoff');
	const authors = t('revision.letter.authors');
	const placeholder = t('revision.letter.placeholder');

	const lines: string[] = [greeting, '', intro, ''];

	const groups = new Map<IssueCategory, RevisionEntry[]>();
	for (const entry of entries) {
		const list = groups.get(entry.issue.category) ?? [];
		list.push(entry);
		groups.set(entry.issue.category, list);
	}

	for (const category of CATEGORY_ORDER) {
		const group = groups.get(category);
		if (!group?.length) continue;
		lines.push(`## ${t(`category.${category}`)} (${t('revision.letter.addressed', { n: group.length })})`, '');
		group.forEach((entry, index) => {
			const text = localized(entry.issue);
			lines.push(`${index + 1}. ${t('revision.letter.finding')}: ${text.title} — ${text.detail}${location(entry.issue)}`);
			lines.push(
				`   ${t('revision.letter.response')}: ${entry.response.trim() || placeholder}`
			);
			lines.push(`   ${t('revision.letter.status')}: ${t(`revision.status.${entry.status}`)}`, '');
		});
	}

	lines.push(closing, '', signoff, authors, '');
	return lines.join('\n');
}
